/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import { invalidateReviewCaches } from '../utils/homeFeedCache'
import { guideLocationPermission } from '../utils/locationPermission'
import { clearAuthHistory } from '../utils/platform'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import AppBar from '../components/AppBar'
import SearchBar from '../components/SearchBar'
import BottomNav from '../components/BottomNav'
import RankingItem from '../components/RankingItem'
import TextListBottomSheet from '../components/TextListBottomSheet'
import { BOTTOM_NAV_HEIGHT } from '../constants/layout'
import { FEED_NAV_ITEM } from '../types/navItem'
import LoadingSpinner from '../components/LoadingSpinner'
import { rankingApi, RankingItem as ApiRankingItem } from '../api/ranking'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useCategoryStore } from '../stores/categoryIconStore'
import { useLocationStore } from '../stores/locationStore'
import Gap from '../components/Gap'
import { toastUtil } from '../utils/toast'
import { dialogUtil } from '../utils/dialog'
import { reviewApi } from '../api/review'
import { zoneFromScore } from '../api/map'
import { zoneScore, ZONE_COUNT } from '../utils/zoneScore'
import { isApp } from '../utils/platform'
import { MIN_SCORED_USER_REVIEWS_FOR_RANKING } from '../constants/reviewComparison'

/**
 * 목록이 갈아끼워지거나 이미지가 늦게 도착해 위치가 밀릴 때, 따라가 줄 시간(ms).
 *
 * 실측에서 캐시 목록 → 서버 목록 교체가 120~600ms 사이에 일어났다.
 * 느린 회선을 감안해 넉넉히 잡는다.
 */
const HIGHLIGHT_FOLLOW_MS = 2500

/** 방금 기록한 가게를 강조해 보여주는 시간(ms) */
const HIGHLIGHT_MS = 2600

/** 강조 배경색. 브랜드 톤 안에서 옅게 — 읽는 데 방해되지 않을 만큼만. */
const HIGHLIGHT_COLOR = 'rgba(107, 94, 70, 0.10)'

/**
 * 임시 진단 — 기록 후 그 가게로 스크롤하는 게 "가끔" 안 된다는 신고.
 * 어디서 끝났는지(행을 못 찾음 / 사용자가 스크롤 / 시간 초과) 남긴다.
 * 원인을 잡으면 지운다.
 */
const traceHighlight = (reason: string, detail: Record<string, unknown>) => {
  void import('../api/base/apiClient').then(({ default: client }) =>
    client
      .post(
        '/diagnostics/trace',
        { context: 'ranking-highlight', detail: { reason, ...detail } },
        { skipErrorHandler: true } as never
      )
      .catch(() => {})
  )
}

const MainRanking: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  // 타 유저 랭킹 조회용 userId (없으면 본인 랭킹)
  const viewUserId = searchParams.get('userId') || undefined
  /**
   * 기록을 막 마치고 넘어왔을 때, 화면에 띄워줄 장소 id.
   *
   * 마운트 시점의 URL에서 한 번만 읽어 붙잡아 둔다.
   * 이 화면은 곧바로 setSearchParams로 category·userId만 남기고 URL을
   * 덮어쓰기 때문에, 계속 searchParams에서 읽으면 목록이 도착하기도 전에
   * highlight가 사라져 스크롤할 대상을 잃는다.
   */
  const highlightPlaceIdRef = useRef<string | undefined>(
    new URLSearchParams(window.location.search).get('highlight') || undefined
  )
  const highlightPlaceId = highlightPlaceIdRef.current

  /**
   * 강조를 아직 보여줄지.
   *
   * 스크롤만 해두면 어느 줄이 내 가게인지 알 수 없다. 잠깐 배경색을 달리해
   * 눈에 띄게 한 뒤 조용히 사라지게 한다. 계속 남으면 목록을 볼 때 방해가 된다.
   */
  const [isHighlightVisible, setIsHighlightVisible] = useState(
    Boolean(highlightPlaceId)
  )

  /**
   * 강조를 언제부터 재느냐.
   *
   * 예전에는 화면이 뜨자마자 2.6초를 셌다. 그런데 그 사이 목록을 받아오고
   * 해당 행으로 스크롤하는 시간이 들어간다. 로딩이 1초쯤 걸리면 정작 행이
   * 보이는 순간엔 이미 1.6초밖에 안 남고, 더 느리면 도착하기도 전에 꺼진다.
   * "강조가 제대로 안 뜬다"의 정체다.
   *
   * 그래서 행을 실제로 찾아 스크롤한 시점부터 센다.
   */
  const [highlightShownAt, setHighlightShownAt] = useState<number | null>(null)

  useEffect(() => {
    if (!highlightPlaceId || highlightShownAt === null) {
      return
    }
    const timer = setTimeout(() => setIsHighlightVisible(false), HIGHLIGHT_MS)

    return () => clearTimeout(timer)
  }, [highlightPlaceId, highlightShownAt])
  const isOtherUserRanking = !!viewUserId
  const getCategoryIconBrown = useCategoryStore(
    (state) => state.getCategoryIconBrown
  )
  const getCategoryName = useCategoryStore((state) => state.getCategoryName)
  const getAllCategories = useCategoryStore((state) => state.getAllCategories)
  const isCategoryInitialized = useCategoryStore((state) => state.isInitialized)
  const [isLoadingInitialRankings, setIsLoadingInitialRankings] =
    useState(false)
  const [allRankings, setAllRankings] = useState<ApiRankingItem[]>([]) // 서버에서 받아온 전체 데이터
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({})

  /**
   * 검색어. 바로 아래 카테고리와 같은 이유로 **주소에 함께 둔다.**
   *
   * useState 만 쓰면, 가게를 검색해 찾아 놓고 그 기록을 눌러 자세히보기로
   * 갔다 돌아왔을 때 화면이 새로 만들어지면서 검색이 풀려 전체 목록이 떴다.
   * 그러면 저장해 둔 스크롤 위치가 전혀 다른 목록에 적용돼 엉뚱한 자리로 간다.
   * (경험한 장소 목록 화면 Reviews.tsx 에서 먼저 같은 문제를 고쳤다)
   */
  const [searchValue, setSearchValue] = useState<string | undefined>(
    () => searchParams.get('q') || undefined
  )
  const [filteredRankings, setFilteredRankings] = useState<ApiRankingItem[]>([])

  /**
   * 목록 스크롤 위치 보존.
   *
   * 랭킹에서 장소를 눌러 리뷰를 보고 뒤로 오면 보던 자리에 그대로 있어야 한다.
   * 매번 맨 위로 돌아가면 스크롤을 한참 내려 보던 사람은 다시 찾아 내려가야 한다.
   * 피드(MainHome)가 쓰는 방식과 같다.
   *
   * 키에 userId를 넣는 이유: 내 랭킹과 남의 랭킹은 서로 다른 목록이라
   * 위치를 공유하면 엉뚱한 자리로 간다.
   */
  const rankingScrollRef = useRef<HTMLDivElement | null>(null)

  /**
   * 목록 컨테이너가 화면에 붙은 횟수. 스크롤 복원 effect의 의존성으로 쓴다.
   *
   * 왜 필요한가
   * ----------
   * 목록은 isRestoringScroll 인 동안 visibility:hidden 으로 감춰지고, 그걸
   * 되돌리는 건 복원 effect뿐이다. 그런데 그 effect는 컨테이너가 없으면
   * (rankingScrollRef.current === null) 아무것도 안 하고 빠져나간다.
   *
   * 컨테이너는 '기록 0곳' 분기에서 아예 렌더되지 않는다. 그리고 그 판정에
   * 쓰는 categoryCounts 는 처음에 {} 라 allRankings 필터 결과로 떨어지는데,
   * 분류가 비동기로 정해지는 동안 잠깐 0이 된다. 그 순간 effect가 헛돌고,
   * 곧이어 컨테이너가 생겨도 의존성이 그대로라 다시 돌지 않는다.
   * 결과는 '기록이 있는데 아무것도 안 보이는' 빈 화면이다.
   *
   * ref는 바뀌어도 다시 그리지 않으므로, 붙는 순간을 state로 알린다.
   */
  const [scrollElAttachCount, setScrollElAttachCount] = useState(0)
  const attachRankingScroll = useCallback((node: HTMLDivElement | null) => {
    rankingScrollRef.current = node
    if (node) setScrollElAttachCount((n) => n + 1)
  }, [])
  const hasRestoredScrollRef = useRef(false)

  /**
   * 강조 스크롤을 끝냈는지.
   *
   * 이 경로는 목록이 갈아끼워질 수 있어 '끝났다' 표시를 미룬다. 그래서 목록이
   * 바뀔 때마다 effect가 다시 돈다. 따라가는 시간(2.5초)이 지나면 여기서 잠가,
   * 그 뒤 사용자가 카테고리를 바꾸거나 목록이 갱신돼도 화면을 잡아채지 않게 한다.
   */
  const highlightDoneRef = useRef(false)
  /*
   * 스크롤 위치 열쇠. 카테고리와 검색어까지 넣는다.
   *
   * 하나로 쓰면, 결과가 두어 개뿐인 검색 목록에서 잡은 위치를 수십 개짜리
   * 전체 목록이 물려받아 엉뚱한 자리로 간다. 목록이 달라지면 열쇠도 달라야 한다.
   */
  const scrollKeyOf = (uid: string | undefined, cat: string, q: string) =>
    `ranking_scroll_position:${uid ?? 'me'}:${cat || 'all'}${q ? `:q=${q}` : ''}`

  const scrollStorageKey = scrollKeyOf(
    viewUserId,
    searchParams.get('category') ?? '',
    (searchParams.get('q') ?? '').trim()
  )
  /**
   * 복원할 위치가 있으면 그 자리로 옮기기 전까지 목록을 감춘다.
   *
   * 감추지 않으면 맨 위가 한 번 그려진 뒤 scrollTop이 적용돼, 사용자 눈에는
   * 상단이 번쩍였다가 원래 자리로 튀는 것처럼 보인다. 애초에 보이면 안 된다.
   */
  const [isRestoringScroll, setIsRestoringScroll] = useState(
    () =>
      !!sessionStorage.getItem(
        scrollKeyOf(
          viewUserId,
          searchParams.get('category') ?? '',
          (searchParams.get('q') ?? '').trim()
        )
      )
  )

  /**
   * 목록 자체도 캐시한다.
   *
   * 스크롤 위치만 되살려도 화면은 매번 "불러오는 중"부터 다시 시작한다.
   * 리뷰를 보고 돌아온 사용자 입장에서는 방금 보던 화면이 그대로 있어야 한다.
   * 캐시로 즉시 그린 뒤 서버 응답이 오면 조용히 갈아끼운다.
   */
  const rankingCacheKey = `ranking_data:${viewUserId ?? 'me'}`

  // 스크롤 위치 저장
  useEffect(() => {
    const scrollEl = rankingScrollRef.current
    if (!scrollEl) return
    const handleScroll = () => {
      sessionStorage.setItem(scrollStorageKey, String(scrollEl.scrollTop))
    }
    scrollEl.addEventListener('scroll', handleScroll, { passive: true })
    return () => scrollEl.removeEventListener('scroll', handleScroll)
  }, [scrollStorageKey, filteredRankings.length])

  // 목록이 그려지자마자, 브라우저가 화면에 칠하기 전에 위치를 잡는다.
  //
  // useEffect + requestAnimationFrame으로 하면 이미 한 번 그린 뒤에 실행되어
  // 상단이 한 프레임 노출된다(느린 기기일수록 눈에 띈다). useLayoutEffect는
  // DOM 반영 후 페인트 직전에 동기로 돌기 때문에 그 틈이 없다.
  useLayoutEffect(() => {
    if (hasRestoredScrollRef.current) return

    /**
     * 목록이 비어 있을 때.
     *
     * 예전에는 그냥 return 했다. 그런데 목록은 isRestoringScroll 인 동안
     * visibility:hidden 으로 숨겨져 있고, 그 값을 되돌리는 건 아래 finish()
     * 뿐이다. 그래서 목록이 끝내 비어 있으면 화면이 영영 감춰진 채로 남았다
     * — '아직 기록이 없습니다' 같은 안내조차 안 보이는 빈칸이 된다.
     *
     * 실제로 기록이 0건인 분류가 있다(예: 술집 0건). 탭을 옮기면 바로 그 상태다.
     *
     * 불러오는 중이면 계속 감춰둔다(그때 보여주면 상단이 번쩍인다).
     * 다 불러왔는데 비어 있으면 되살릴 위치도 없으니 그냥 보여준다.
     */
    if (filteredRankings.length === 0) {
      // 감춰둔 걸 되돌리기만 한다.
      //
      // hasRestoredScrollRef 는 켜지 않는다. 그걸 켜면 이 effect의 첫 줄에서
      // 곧바로 return 하게 되어, 목록이 도착한 뒤에도 스크롤 복원과 하이라이트
      // 갈래에 아예 도달하지 못한다. isLoadingInitialRankings 의 초기값이
      // false라 '마운트 직후 0건' 상태가 반드시 한 번 지나가므로, 켜는 순간
      // 기록 직후 랭킹 강조가 통째로 사라진다(실제로 그렇게 됐다).
      if (!isLoadingInitialRankings) {
        setIsRestoringScroll(false)
      }
      return
    }

    const el = rankingScrollRef.current
    if (!el) return

    const finish = () => {
      hasRestoredScrollRef.current = true
      setIsRestoringScroll(false)
    }

    /**
     * 기록을 막 마치고 넘어온 경우: 그 가게 행이 보이게 한다.
     *
     * 아래의 '저장된 위치 복원'과 절대 섞지 않는다. 그쪽은 목표 scrollTop을
     * 직접 계산해 맞을 때까지 재시도하면서 그동안 목록을 감춰두는데,
     * 한 번이라도 어긋나면 2초 동안 빈 화면이 보인다. 실제로 그렇게 됐다.
     *
     * 여기서는 계산을 브라우저에 맡긴다(scrollIntoView). 중첩 스크롤이나
     * flex 레이아웃까지 브라우저가 알아서 처리하므로 어긋날 여지가 없다.
     * useLayoutEffect라 화면에 칠하기 전에 끝나 깜빡임도 없다.
     */
    if (highlightPlaceId && !highlightDoneRef.current) {
      // 목록을 보여주는 게 먼저다. 못 찾아도 감춰두지 않는다.
      setIsRestoringScroll(false)

      /**
       * 매번 다시 찾아서 스크롤한다. 행을 붙잡아 두면 안 된다.
       *
       * 랭킹은 캐시된 옛 목록을 먼저 그리고, 서버 응답이 오면 갈아끼운다.
       * 새 기록이 끼어들면 그 아래 순위가 한 칸씩 밀리고, 행 key가
       * `${id}-${index}` 라 index가 바뀌면서 React가 노드를 전부 새로 만든다.
       * 그래서 처음에 잡아둔 행은 곧 DOM에서 떨어져 나간다
       * (실측: 600ms 시점에 isConnected=false, getBoundingClientRect가 전부 0).
       *
       * 떨어져 나간 노드에 scrollIntoView를 불러봐야 아무 일도 일어나지 않는다.
       * 잡아두지 말고 그때그때 다시 찾는다.
       */
      const bring = (): boolean => {
        const row = el.querySelector<HTMLElement>(
          `[data-place-id="${highlightPlaceId}"]`
        )
        if (!row) {
          return false
        }
        row.scrollIntoView({ block: 'center' })

        return true
      }

      let foundOnce = bring()
      if (foundOnce) setHighlightShownAt((v) => v ?? Date.now())

      /**
       * 목록이 갈아끼워지는 순간을 잡아 다시 스크롤한다.
       *
       * 크기 변화(ResizeObserver)만으로는 못 잡는다. 실측에서 목록이 통째로
       * 교체됐는데도 행 개수(130)와 전체 높이(10529)가 그대로였다.
       * 새 기록이 끼어들어도 개수가 같으면 높이가 안 변하기 때문이다.
       * 그래서 자식 노드가 바뀌는 것 자체를 본다(MutationObserver).
       *
       * 사용자가 직접 스크롤하면 즉시 손을 뗀다 — 조작을 방해하면 안 된다.
       */
      const list = el.firstElementChild ?? el
      const retry = () => {
        if (bring()) {
          foundOnce = true
          setHighlightShownAt((v) => v ?? Date.now())
        }
      }
      const resizeObs = new ResizeObserver(retry)
      const mutationObs = new MutationObserver(retry)
      resizeObs.observe(list)
      mutationObs.observe(list, { childList: true, subtree: true })

      /** 관찰자·리스너만 뗀다. '끝났다' 표시는 하지 않는다. */
      const teardown = () => {
        resizeObs.disconnect()
        mutationObs.disconnect()
        el.removeEventListener('touchstart', onUserScroll)
        el.removeEventListener('wheel', onUserScroll)
        clearTimeout(timer)
      }

      /** 여기서 끝낸다 — 다시 시도하지 않는다. */
      const stop = (reason: string) => {
        if (highlightDoneRef.current) return
        teardown()
        highlightDoneRef.current = true
        // 끝내 못 찾았으면 강조도 접는다 (안 그러면 배경이 계속 깔려 있다)
        if (!foundOnce) setIsHighlightVisible(false)
        traceHighlight(reason, {
          placeId: highlightPlaceId,
          found: foundOnce,
          rows: filteredRankings.length,
        })
      }

      /**
       * 사용자가 스크롤하면 손을 뗀다 — 다만 아직 한 번도 못 찾았으면 계속 기다린다.
       *
       * 예전에는 찾았든 못 찾았든 화면을 스치기만 하면 즉시 포기했다.
       * 목록이 오는 동안(캐시된 옛 목록 → 서버 응답으로 교체) 손가락이 닿으면
       * 그대로 끝나버려서, 정작 보여줘야 할 순간에 아무 일도 안 일어났다.
       */
      const onUserScroll = () => {
        if (foundOnce) stop('user-scroll')
      }
      el.addEventListener('touchstart', onUserScroll, { passive: true })
      el.addEventListener('wheel', onUserScroll, { passive: true })
      const timer = setTimeout(
        () => stop(foundOnce ? 'timeout-after-found' : 'timeout-not-found'),
        HIGHLIGHT_FOLLOW_MS
      )

      /**
       * 목록이 바뀌면 이 effect가 다시 돌아야 하므로 '끝났다' 표시는 하지 않는다.
       *
       * 예전에는 여기서 stop()을 불렀고, stop()이 그 표시를 켰다. 주석과 코드가
       * 반대였다. 의존성에 filteredRankings.length 가 있어서, 캐시된 옛 목록이
       * 서버 목록으로 교체되면(=새 기록이 끼어들어 길이가 바뀌면) 이 정리가
       * 먼저 돌며 표시를 켜버렸고, 이어서 다시 돈 effect는 하이라이트 갈래를
       * 통째로 건너뛰었다. 신규 기록은 캐시 목록에 그 가게가 없어 첫 시도가
       * 반드시 실패하므로, 결국 한 번도 못 보여주고 끝났다.
       * (기존 기록 수정은 캐시에 이미 있어서 첫 시도에 성공 — "가끔 된다"의 정체)
       */
      return teardown
    }

    const saved = sessionStorage.getItem(scrollStorageKey)
    if (saved === null) {
      finish()
      return
    }
    const target = parseInt(saved, 10)

    // scrollTop은 콘텐츠 높이를 넘으면 브라우저가 잘라서 넣는다. 행 안의 이미지가
    // 아직 안 와서 높이가 덜 자란 상태면 0에 가깝게 잘리고, 그게 "상단이 보였다가
    // 제자리로 가는" 증상이 된다. 그래서 넣어보고 실제로 그 값이 됐는지 확인한다.
    const applyScroll = () => {
      el.scrollTop = target
      return Math.abs(el.scrollTop - target) <= 1
    }

    if (applyScroll()) {
      finish()
      return
    }

    // 아직 잘린다 → 콘텐츠 높이가 자랄 때마다 다시 시도한다.
    const content = el.firstElementChild ?? el
    const observer = new ResizeObserver(() => {
      if (applyScroll()) {
        observer.disconnect()
        finish()
      }
    })
    observer.observe(content)

    // 끝내 높이가 안 자라는 경우(목록이 짧아 그 위치가 아예 없는 경우 등)
    // 영원히 감춰두지 않는다.
    const timer = setTimeout(() => {
      observer.disconnect()
      finish()
    }, 2000)

    return () => {
      observer.disconnect()
      clearTimeout(timer)
    }
  }, [
    filteredRankings.length,
    scrollStorageKey,
    highlightPlaceId,
    isLoadingInitialRankings,
    scrollElAttachCount,
  ])

  const [isActionModalOpen, setIsActionModalOpen] = useState(false)
  const [selectedRanking, setSelectedRanking] = useState<ApiRankingItem | null>(
    null
  )

  const [isEditMode, setIsEditMode] = useState(false)
  const [isSearchBarVisible, setIsSearchBarVisible] = useState(
    () => !!searchParams.get('q')
  )

  /*
   * 검색어를 주소에 적어 둔다 — 이게 있어야 뒤로 돌아왔을 때 복원된다.
   *
   * 한 박자 늦춘다. 이 화면의 검색은 서버를 부르지 않고 화면에서 거르지만,
   * 글자마다 주소를 고치면 그만큼 다시 그려진다.
   *
   * replace 라 히스토리가 글자 수만큼 쌓이지 않고, 기존 파라미터를 복사해
   * category·userId 를 보존한다. 같은 값이면 쓰지 않는다 — 안 막으면
   * setSearchParams 가 만든 새 객체 때문에 자기 자신을 다시 부른다.
   */
  useEffect(() => {
    const id = setTimeout(() => {
      const current = searchParams.get('q') ?? ''
      const next = (searchValue ?? '').trim()
      if (current === next) return
      const params = new URLSearchParams(searchParams)
      if (next) params.set('q', next)
      else params.delete('q')
      setSearchParams(params, { replace: true })
    }, 300)
    return () => clearTimeout(id)
  }, [searchValue, searchParams, setSearchParams])
  const [isCategoryBottomSheetOpen, setIsCategoryBottomSheetOpen] =
    useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string>(
    () => searchParams.get('category') || ''
  )
  /**
   * selectedCategory 의 최신값.
   *
   * loadRankings 는 await 를 품고 있어서, 그 안에서 읽는 selectedCategory 는
   * 함수가 만들어질 때의 낡은 값이다. 캐시로 먼저 그려 분류를 정해 놓고
   * 서버 응답이 도착하면 그 낡은 값으로 되돌아가 버린다. 정할 때마다 여기에
   * 같이 적어 두고, 비동기 안에서는 이쪽을 본다.
   */
  const selectedCategoryRef = useRef<string>(searchParams.get('category') || '')
  const applySelectedCategory = useCallback((code: string) => {
    selectedCategoryRef.current = code
    setSelectedCategory(code)
  }, [])
  /**
   * '기록이 가장 많은 분류'로 한 번 옮겨도 되는 상태인지.
   *
   * 분류 목록의 첫 칸(레스토랑)을 그냥 쓰면, 카페 기록만 있는 사람의 랭킹은
   * 빈 화면으로 열린다. 기록이 있는 104명 중 13명이 그랬다. 그래서 목록이
   * 도착하면 기록이 가장 많은 분류로 한 번 옮긴다.
   *
   * 주소에 category 가 이미 박혀 있으면 사람이 고른 것이거나 뒤로 돌아온
   * 것이니 건드리지 않는다.
   */
  const autoPickCategoryRef = useRef(
    !new URLSearchParams(window.location.search).get('category')
  )
  /** 마지막으로 목록을 불러온 대상. 대상이 바뀌면 분류도 다시 고른다. */
  const loadedForUserRef = useRef<string | undefined>(undefined)
  const hasLoadedOnceRef = useRef(false)
  const [isSortBottomSheetOpen, setIsSortBottomSheetOpen] = useState(false)

  // 정렬 타입 정의
  type SortType = 'rating' | 'date' | 'distance'
  const [sortType, setSortType] = useState<SortType>('rating')

  // 사용자 위치 (스토어에서 가져오기)
  const userLocation = useLocationStore((state) => state.location)
  const getUserLocation = useLocationStore((state) => state.getUserLocation)
  const requestLocationPermission = useLocationStore(
    (state) => state.requestLocationPermission
  )

  useEffect(() => {
    clearAuthHistory()

    // 카테고리가 초기화되었는지 확인
    if (isCategoryInitialized) {
      const categories = getAllCategories()
      if (categories.length > 0) {
        // URL 쿼리 파라미터의 카테고리가 유효한지 확인
        const queryCategory = searchParams.get('category')
        const isValidQuery = queryCategory && categories.some((cat) => cat.code === queryCategory)
        if (!isValidQuery) {
          // 유효하지 않거나 없으면 첫 번째 카테고리로 설정
          const firstCode = categories[0].code
          applySelectedCategory(firstCode)
          // 통째로 새로 만들면 검색어(q) 같은 다른 값이 지워진다. 복사해서 고친다.
          const nextParams = new URLSearchParams(searchParams)
          nextParams.set('category', firstCode)
          if (viewUserId) nextParams.set('userId', viewUserId)
          setSearchParams(nextParams, { replace: true })
        }
        // 유효한 경우 이미 state 초기값으로 반영되어 있으므로 별도 처리 불필요
      }
      // 랭킹 로드는 아래 [isCategoryInitialized, viewUserId] effect가 담당
      // (본인↔타 유저 전환 시에도 재조회되도록 분리)
    }

    // 위치 권한은 랭킹 진입 시 요청하지 않는다(지도 탭에서만 요청).
    // 이미 위치가 있으면(지도서 허용) 그대로 쓰고, 없으면 '거리순 정렬' 선택 시에만 요청.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCategoryInitialized])

  // 랭킹 로드: 카테고리 초기화 후 + 본인↔타 유저(viewUserId) 전환 시 재조회.
  // (타 유저 랭킹 → 하단 랭킹 탭으로 내 랭킹 전환 시 같은 라우트라 컴포넌트가
  //  유지돼 데이터가 안 바뀌던 버그 수정 — 기록이 있어도 빈 상태로 뜨던 문제)
  useEffect(() => {
    if (!isCategoryInitialized) return
    // 보던 사람이 바뀌었다면 그 사람 기준으로 분류를 다시 고른다.
    // (같은 라우트라 컴포넌트가 유지돼서 ref 가 살아 있다)
    if (hasLoadedOnceRef.current && loadedForUserRef.current !== viewUserId) {
      autoPickCategoryRef.current = true
    }
    loadedForUserRef.current = viewUserId
    hasLoadedOnceRef.current = true
    loadRankings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCategoryInitialized, viewUserId])

  // selectedCategory가 변경되면 URL 쿼리 파라미터 업데이트 + 필터링 다시 적용
  useEffect(() => {
    if (selectedCategory) {
      // 위와 같은 이유로 복사해서 고친다 (이 effect 는 화면에 들어올 때도 한 번 돈다)
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set('category', selectedCategory)
      if (viewUserId) nextParams.set('userId', viewUserId)
      setSearchParams(nextParams, { replace: true })
    }
    if (selectedCategory && allRankings.length > 0) {
      applyFiltersAndSort(allRankings, selectedCategory, searchValue, sortType)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory])

  // 두 좌표 간 거리 계산 (Haversine 공식, 미터 단위)
  const calculateDistance = (
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number => {
    const R = 6371000 // 지구 반지름 (미터)
    const dLat = ((lat2 - lat1) * Math.PI) / 180
    const dLng = ((lng2 - lng1) * Math.PI) / 180
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  /**
   * 기록이 가장 많은 분류의 코드. 하나도 없으면 null.
   *
   * 동률이면 분류 목록에서 앞선 쪽(레스토랑 → 카페 → 술집). 목록에 없는
   * 코드는 세지 않는다 — 고를 수 없는 분류로 보내면 또 빈 화면이 된다.
   */
  const categoryWithMostRecords = (
    rows: ApiRankingItem[]
  ): string | null => {
    if (rows.length === 0) return null
    const counts = new Map<string, number>()
    rows.forEach((row) => {
      counts.set(row.category, (counts.get(row.category) ?? 0) + 1)
    })
    let best: string | null = null
    let bestCount = 0
    getAllCategories().forEach((category) => {
      const n = counts.get(category.code) ?? 0
      if (n > bestCount) {
        best = category.code
        bestCount = n
      }
    })
    return best
  }

  /**
   * 목록을 받은 뒤 보여줄 분류를 정한다.
   *
   * 자동으로 옮겨도 되는 상태면 기록이 가장 많은 분류로, 아니면 지금 분류
   * 그대로. 기록이 하나도 없으면 옮길 데가 없으니 지금 분류를 유지하고
   * 자동 이동 권한도 남겨 둔다 (나중에 목록이 도착하면 그때 옮긴다).
   */
  const resolveCategoryFor = (rows: ApiRankingItem[]): string => {
    const current =
      selectedCategoryRef.current || getAllCategories()[0]?.code || ''
    if (!autoPickCategoryRef.current) return current

    /*
     * 방금 기록한 가게를 보여주러 들어온 길이면(highlight), 그 가게가 들어
     * 있는 분류가 1순위다. 기록이 가장 많은 분류로 보내 버리면 정작 보러 온
     * 가게가 그 목록에 없다. (보통은 ReviewComplete 가 category 를 붙여
     * 주므로 여기까지 오지 않지만, 분류를 모르고 넘어오는 길도 있다.)
     */
    if (highlightPlaceId) {
      const row = rows.find(
        (r) => String(r.placeId) === String(highlightPlaceId)
      )
      if (row?.category) {
        autoPickCategoryRef.current = false
        return row.category
      }
    }

    const best = categoryWithMostRecords(rows)
    if (!best) return current
    autoPickCategoryRef.current = false
    return best
  }

  // 초기 로드 시 전체 데이터를 한 번만 서버에서 가져옴
  const loadRankings = async () => {
    // 캐시가 있으면 먼저 그린다 — 뒤로 돌아왔을 때 로딩 화면을 거치지 않는다.
    let hasCache = false
    try {
      const cached = sessionStorage.getItem(rankingCacheKey)
      if (cached) {
        const parsed = JSON.parse(cached) as ApiRankingItem[]
        if (Array.isArray(parsed) && parsed.length > 0) {
          hasCache = true
          setAllRankings(parsed)
          const cat = resolveCategoryFor(parsed)
          if (cat !== selectedCategoryRef.current) applySelectedCategory(cat)
          applyFiltersAndSort(parsed, cat)
        }
      }
    } catch {
      /* 캐시가 깨졌으면 무시하고 평소대로 불러온다 */
    }

    if (!hasCache) setIsLoadingInitialRankings(true)

    try {
      // 카테고리 파라미터 없이 전체 데이터 가져오기 (타 유저면 해당 유저 랭킹)
      const response = await rankingApi.getRankings(undefined, viewUserId)
      setAllRankings(response.rankings)
      try {
        sessionStorage.setItem(
          rankingCacheKey,
          JSON.stringify(response.rankings)
        )
      } catch {
        /* 용량 초과 등은 캐시 없이 동작하면 되므로 무시 */
      }
      if (response.categoryCounts) {
        setCategoryCounts(response.categoryCounts)
      }

      // 초기 정렬 및 필터링 적용.
      // 분류는 resolveCategoryFor 가 정한다 — 기록이 가장 많은 쪽으로 한 번
      // 옮기고, 그 뒤로는 지금 분류를 그대로 쓴다.
      const categoryToUse = resolveCategoryFor(response.rankings)
      if (categoryToUse !== selectedCategoryRef.current) {
        applySelectedCategory(categoryToUse)
      }
      applyFiltersAndSort(response.rankings, categoryToUse)
    } catch (error) {
      console.error('랭킹 목록 조회 실패:', error)
      // 캐시로 이미 그려둔 게 있으면 지우지 않는다(빈 화면으로 되돌아가는 것 방지)
      if (!hasCache) {
        setAllRankings([])
        setFilteredRankings([])
      }
    } finally {
      setIsLoadingInitialRankings(false)
    }
  }

  // 카테고리 및 검색어 필터 적용 + 정렬 (로컬 필터링만 사용, 서버 요청 없음)
  const applyFiltersAndSort = (
    rankingsToFilter: ApiRankingItem[] = allRankings,
    currentCategory: string = selectedCategory,
    currentSearchValue: string | undefined = searchValue,
    currentSortType: SortType = sortType
  ) => {
    let filtered = [...rankingsToFilter]

    // 카테고리 필터 적용 (카테고리가 선택된 경우에만)
    if (currentCategory) {
      filtered = filtered.filter((ranking) => {
        // 백엔드에서 오는 category는 원본 이넘 값이므로 직접 비교
        return ranking.category === currentCategory
      })
    }

    // 검색어 필터 적용
    if (currentSearchValue !== undefined && currentSearchValue !== '') {
      filtered = filtered.filter((ranking) => {
        const searchLower = currentSearchValue.toLowerCase()
        const categoryLabel = getCategoryName(ranking.category)
        return (
          ranking.name.toLowerCase().includes(searchLower) ||
          ranking.address.toLowerCase().includes(searchLower) ||
          categoryLabel.toLowerCase().includes(searchLower)
        )
      })
    }

    // 정렬 적용
    switch (currentSortType) {
      case 'rating':
        filtered.sort((a, b) => {
          // Review의 score(rating) 기준으로 정렬
          return b.rating - a.rating
        })
        break
      case 'date':
        filtered.sort((a, b) => {
          const dateA = a.latestReviewDate
            ? new Date(a.latestReviewDate).getTime()
            : 0
          const dateB = b.latestReviewDate
            ? new Date(b.latestReviewDate).getTime()
            : 0
          return dateB - dateA
        })
        break
      case 'distance':
        // 거리 순 정렬 (사용자 위치가 있어야 함)
        if (userLocation) {
          filtered.sort((a, b) => {
            // 위치 정보가 없는 항목은 뒤로
            if (!a.lat || !a.lng) return 1
            if (!b.lat || !b.lng) return -1

            const distanceA = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              a.lat,
              a.lng
            )
            const distanceB = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              b.lat,
              b.lng
            )
            return distanceA - distanceB
          })
        }
        // 위치 정보가 없으면 정렬하지 않음 (거리순 선택 시 위치 정보 요청됨)
        break
    }

    setFilteredRankings(filtered)
  }

  // 정렬 옵션 라벨 가져오기
  const getSortLabel = (sort: SortType): string => {
    switch (sort) {
      case 'rating':
        return '점수'
      case 'date':
        return '추가한 날짜'
      case 'distance':
        return '거리 순'
      default:
        return '점수'
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px 이상 이동해야 드래그 시작 (스크롤과 구분)
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100, // 100ms 지연 후 드래그 시작 (앱 WebView 터치 반응)
        tolerance: 8, // 8px 이동 허용
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleEditClick = () => {
    // 3개 미만인 경우 수정 불가
    if (allRankings.length < MIN_SCORED_USER_REVIEWS_FOR_RANKING) {
      toastUtil.warning('3개 이상 기록을 남기신 후 수정할 수 있습니다')
      return
    }

    if (isEditMode) {
      // 저장 모드
      handleSave()
    } else {
      // 수정 모드 진입
      // 검색어 초기화 및 검색바 숨김
      setSearchValue(undefined)
      setIsSearchBarVisible(false)
      // 평점 정렬로 변경
      setSortType('rating')
      // 평점 정렬 적용
      applyFiltersAndSort(allRankings, selectedCategory, searchValue, 'rating')
      setIsEditMode(true)
    }
  }

  const handleSearchIconClick = () => {
    // 검색 아이콘 클릭 시 검색바 표시/숨김 토글
    setIsSearchBarVisible(!isSearchBarVisible)
  }

  const handleCategoryClick = () => {
    // 카테고리 드롭다운 클릭 시 바텀 시트 열기
    setIsCategoryBottomSheetOpen(true)
  }

  const handleCategorySelect = (category: string) => {
    // 사람이 직접 골랐으면 더는 자동으로 옮기지 않는다
    autoPickCategoryRef.current = false
    // 카테고리 선택 시 로컬 필터링만 적용 (서버 요청 없음)
    applySelectedCategory(category)
    setIsCategoryBottomSheetOpen(false)
    // 카테고리 변경 시 로컬 필터링 적용
    applyFiltersAndSort(allRankings, category, searchValue, sortType)
  }

  const handleSortClick = () => {
    setIsSortBottomSheetOpen(true)
  }

  const handleSortSelect = (sort: SortType) => {
    setIsSortBottomSheetOpen(false)

    // 거리순 정렬 선택 시 사용자 위치가 없으면 위치 정보 요청
    if (sort === 'distance' && !userLocation) {
      // 위치 정보 요청
      if (isApp() && (window as any).FlutterBridge) {
        // 앱 환경에서는 위치 권한을 먼저 요청
        requestLocationPermission().then((granted) => {
          if (!granted) {
            // 거부됐는데 아무 말도 없으면 정렬이 왜 안 되는지 알 수 없다
            guideLocationPermission()
            return
          }
          getUserLocation().then(() => {
            // 위치 정보 획득 후 거리순 정렬 적용
            setSortType('distance')
            applySorting('distance')
          })
        })
      } else {
        // 웹 환경에서는 바로 위치 정보 요청
        getUserLocation().then(() => {
          // 위치 정보 획득 후 거리순 정렬 적용
          setSortType('distance')
          applySorting('distance')
        })
      }
      return
    }

    setSortType(sort)
    // 정렬 적용
    applySorting(sort)
  }

  // 정렬 적용 함수
  const applySorting = (sort: SortType) => {
    // 전체 필터링 및 정렬 재적용
    applyFiltersAndSort(allRankings, selectedCategory, searchValue, sort)
  }

  const handleSave = async () => {
    // 정렬 가드: 점수 순위 저장은 '평점 정렬' 상태에서만 허용.
    // (날짜/거리 정렬 순서가 점수 순위로 저장되어 점수가 뒤섞이는 사고 방지)
    if (sortType !== 'rating') {
      toastUtil.warning('점수 순서는 평점 정렬 상태에서만 저장할 수 있습니다')
      return
    }

    try {
      // 현재 순서대로 rank와 점수 정보를 백엔드에 전송
      const rankingsToSave = filteredRankings.map((ranking, index) => ({
        id: ranking.id,
        rank: index + 1,
        // 끌어 옮기면서 존이 바뀔 수 있다. 화면에서 본 그대로 저장되도록 함께 보낸다.
        zone: ranking.zone ?? zoneFromScore(ranking.rating),
      }))

      await rankingApi.updateOrder({
        rankings: rankingsToSave,
      })

      // 저장 성공 시 서버에서 최신 데이터 다시 가져오기
      const response = await rankingApi.getRankings()
      setAllRankings(response.rankings)
      if (response.categoryCounts) {
        setCategoryCounts(response.categoryCounts)
      }

      // 현재 필터 상태 유지하면서 필터링 및 정렬 재적용
      applyFiltersAndSort(
        response.rankings,
        selectedCategory,
        searchValue,
        sortType
      )

      setIsEditMode(false)
    } catch (error) {
      console.error('랭킹 순서 저장 실패:', error)
      setIsEditMode(false)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setFilteredRankings((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)

        // 순서 변경
        const reordered = arrayMove(items, oldIndex, newIndex)

        /*
         * 옮긴 자리에 맞춰 존과 점수를 다시 정한다.
         *
         * 예전에는 점수를 자기 존 안에서만 돌려썼다. 그래서 '좋아' 1등을
         * '인생맛집' 사이로 끌어올려도 점수가 그대로여서, 43위 8.0 / 44위 8.1처럼
         * 아래가 더 높은 화면이 나왔다. 저장하면 제자리로 돌아가기까지 했다.
         *
         * 이제는 놓은 자리가 존을 정한다. 위아래 이웃의 존 사이로 끼워 넣어
         * 목록의 존이 항상 내림차순이 되게 맞춘다.
         */
        const zoneOf = (it: ApiRankingItem) => it.zone ?? zoneFromScore(it.rating)
        const zones = reordered.map(zoneOf)

        // 옮긴 항목만 이웃에 맞춘다. 나머지는 원래 존을 지킨다.
        const movedAt = reordered.findIndex((it) => it.id === active.id)
        if (movedAt >= 0) {
          const above = movedAt > 0 ? zones[movedAt - 1] : ZONE_COUNT - 1
          const below = movedAt < zones.length - 1 ? zones[movedAt + 1] : 0
          // 위 이웃보다 높을 수 없고, 아래 이웃보다 낮을 수 없다
          zones[movedAt] = Math.max(below, Math.min(above, zones[movedAt]))
        }

        const countByZone: Record<number, number> = {}
        zones.forEach((z) => (countByZone[z] = (countByZone[z] ?? 0) + 1))

        const cursor: Record<number, number> = {}
        const updated = reordered.map((item, index) => {
          const z = zones[index]
          const i = cursor[z] ?? 0
          cursor[z] = i + 1
          return {
            ...item,
            rank: index + 1,
            zone: z,
            // 서버와 같은 식으로 계산한다 — 미리보기와 저장 결과가 같아야 한다
            rating: zoneScore(i, countByZone[z], z),
          }
        })

        return updated
      })
    }
  }

  const handleRankingItemClick = (ranking: ApiRankingItem) => {
    if (isEditMode) {
      // 수정 모드에서는 클릭 무시
      return
    }
    // 타 유저 랭킹: 그 유저가 이 장소에 쓴 리뷰를 읽기전용 상세로 표시.
    // (reviewId는 백엔드가 조회 대상 유저 기준으로 내려줌)
    if (isOtherUserRanking) {
      if (ranking.reviewId) {
        navigate(`/review-detail/${ranking.reviewId}`)
      }
      return
    }
    setSelectedRanking(ranking)
    setIsActionModalOpen(true)
  }

  const handleReviewRewrite = () => {
    if (selectedRanking) {
      setIsActionModalOpen(false)
      const redirectUrl = `/main-ranking${selectedCategory ? `?category=${selectedCategory}` : ''}`
      navigate(`/store-review/${selectedRanking.placeId}?redirect=${encodeURIComponent(redirectUrl)}`)
      setSelectedRanking(null)
    }
  }

  // 리뷰 보기 → 내가 쓴 리뷰의 자세히보기 화면으로
  const handleViewReview = () => {
    const ranking = selectedRanking
    if (!ranking?.reviewId) return

    setIsActionModalOpen(false)
    navigate(`/review-detail/${ranking.reviewId}`)
    setSelectedRanking(null)
  }

  // 리뷰 삭제 → 확인 팝업 후 삭제 (바텀시트는 onClose로 닫히므로 selectedRanking 미리 캡처)
  const handleDeleteReview = async () => {
    const ranking = selectedRanking
    if (!ranking?.reviewId) return

    const confirmed = await dialogUtil.confirm({
      message: '기록을 삭제하시겠어요?',
      confirmText: '삭제',
      cancelText: '취소',
    })
    if (!confirmed) return

    try {
      await reviewApi.deleteReview(ranking.reviewId)
      // 지운 기록이 홈 피드·랭킹 캐시에 남아 있으면 계속 보인다. 버린다.
      invalidateReviewCaches()
      toastUtil.success('기록이 삭제되었습니다')
      loadRankings()
    } catch (error) {
      console.error('리뷰 삭제 실패:', error)
      toastUtil.error('기록 삭제 중 오류가 발생했습니다')
    }
  }

  const handleCloseModal = () => {
    setIsActionModalOpen(false)
    setSelectedRanking(null)
  }

  const renderCategoryFilter = () => {
    return (
      <div className="w-full flex items-center justify-center">
        <button
          type="button"
          onClick={handleCategoryClick}
          className="flex items-center"
        >
          {selectedCategory && getCategoryIconBrown(selectedCategory) && (
            <img
              src={getCategoryIconBrown(selectedCategory)!}
              alt="카테고리"
              className="w-28px h-28px object-contain"
            />
          )}

          <Gap width={12} />

          <span className="font-pretendard font-bold text-16px leading-125% text-brown-80%-main text-center">
            {getCategoryName(selectedCategory)}
          </span>

          <Gap width={16} />

          <img
            src="/images/bold-arrow-down-icon.png"
            alt="드롭다운"
            className="w-17px h-32px object-contain"
          />
        </button>
      </div>
    )
  }

  const renderFilterSection = () => {
    return (
      <div className="w-full flex items-center px-18px justify-between">
        {/* 정렬 */}
        <button
          type="button"
          onClick={handleSortClick}
          className="flex items-center"
        >
          <img
            src="/images/filter-sort-icon.png"
            alt="정렬"
            className="w-28px h-28px object-contain"
          />
          <span className="font-pretendard font-regular text-16px leading-125% text-[#4B3621] text-left">
            {getSortLabel(sortType)}
          </span>
        </button>

        {/* 검색 아이콘 */}
        <button
          type="button"
          onClick={handleSearchIconClick}
          className="flex items-center justify-center"
        >
          <img
            src="/images/ranking-search-icon.png"
            alt="검색"
            className="w-28px h-28px object-contain"
          />
        </button>
      </div>
    )
  }

  const renderSearchBar = () => {
    return (
      <div className="w-full min-w-0 px-20px flex pt-16px pb-8px items-center">
        <div className="min-w-0 flex-1">
          <SearchBar
            placeholder="검색"
            value={searchValue}
            onSearch={(value) => {
              setSearchValue(value)
              // 검색어 변경 시 로컬 필터링만 적용 (서버 요청 없음)
              applyFiltersAndSort(allRankings, selectedCategory, value, sortType)
            }}
          />
        </div>

        <Gap width={16} />

        {/* 닫기 */}
        <div
          className="font-pretendard font-bold text-16px leading-24px text-brown-80%-main cursor-pointer flex-shrink-0"
          onClick={() => {
            setIsSearchBarVisible(false)
            setSearchValue('')
            applyFiltersAndSort(allRankings, selectedCategory, '', sortType)
          }}
        >
          닫기
        </div>
      </div>
    )
  }

  const renderLoadingSpinner = () => {
    return (
      <div className="flex-1 w-full flex items-center justify-center empty-state-center">
        <LoadingSpinner />
      </div>
    )
  }

  const renderEmptyRanking = () => {
    // "3곳 기록하고 랭킹 보기"는 본인 랭킹에서만. 타 유저 랭킹이 비면 아무 텍스트도 안 띄움.
    if (isOtherUserRanking) {
      return null
    }
    return (
      <div className="flex-1 w-full flex flex-col items-center justify-center empty-state-center">
        {/* 0곳일 때: 가운데 텍스트만. 탭하면 장소 기록(검색)으로 이동 */}
        <button
          type="button"
          onClick={handleExperienceRecordClick}
          className="font-pretendard font-regular text-16px leading-180% text-textColorText2 text-center"
        >
          3곳 기록하고 랭킹 보기
        </button>
      </div>
    )
  }

  const handleExperienceRecordClick = () => {
    navigate(
      `/search?filter=place&hideTab=true&placeholder=${encodeURIComponent(
        '기록할 장소를 검색해주세요'
      )}`
    )
  }

  const renderRankingList = () => {
    const categoryItems = allRankings.filter(
      (r) => r.category === selectedCategory
    )
    const currentCategoryCount = categoryCounts[selectedCategory] ?? categoryItems.length
    // 0곳: 빈 화면("3곳 기록하고 랭킹 보기"). 1곳 이상이면 랭킹 리스트 표시.
    const isCategoryEmpty = currentCategoryCount === 0
    // 점수는 3곳 미만(1~2곳)이면 숨김("?점"), 3곳부터 표시.
    const shouldHideScore = currentCategoryCount < MIN_SCORED_USER_REVIEWS_FOR_RANKING

    // 카테고리 '안에서의' 점수 등수 맵 (전역 ranking.rank가 아니라 카테고리 내 1~N).
    // 점수정렬과 동일한 기준(점수 내림차순, 동점은 입력 순서 유지)으로 매긴다.
    const categoryRankMap = new Map<string, number>()
    ;[...categoryItems]
      .sort((a, b) => b.rating - a.rating)
      .forEach((item, i) => categoryRankMap.set(item.id, i + 1))

    return (
      <div className="w-full min-w-0 flex-1 flex flex-col overflow-hidden scrollbar-hide">
        <Gap height={16} />

        {/* 카테고리 필터 */}
        {renderCategoryFilter()}

        {isCategoryEmpty ? (
          renderEmptyRanking()
        ) : (
          <>
            {/* 필터/정렬 섹션 (수정 모드가 아닐 때만 표시) */}
            {!isEditMode && renderFilterSection()}

            {/* 검색바 (검색 아이콘 클릭 시 표시) */}
            {!isEditMode && isSearchBarVisible && renderSearchBar()}

            {/* 랭킹 목록 */}
            <div
              ref={attachRankingScroll}
              className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto scrollbar-hide flex"
              style={
                isRestoringScroll ? { visibility: 'hidden' } : undefined
              }
            >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredRankings.map((r) => r.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="w-full flex flex-col">
                {filteredRankings.map((ranking, index) => (
                  <div
                    key={`${ranking.id}-${index}`}
                    // 기록을 막 마치고 넘어온 경우, 이 표시로 그 가게 행을 찾아
                    // 화면에 띄운다(아래 스크롤 useLayoutEffect 참고).
                    data-place-id={ranking.placeId}
                    className="w-full flex flex-col relative isolate"
                  >
                    {/*
                      방금 기록한 가게 강조.

                      행 자체에 배경색을 주면 화면 좌우 끝까지 칠해져, 20px 안쪽에
                      정렬된 구분선·내용과 어긋나 보인다. 그래서 구분선과 같은 폭으로
                      깔개를 하나 깔고 내용 뒤에 둔다(-z-10 + isolate).

                      바깥 크기를 건드리지 않으므로 행 높이나 드래그 정렬에 영향이 없다.
                      opacity로 사라지게 해야 부드럽게 빠진다 — 요소를 없애면 툭 끊긴다.
                    */}
                    {String(ranking.placeId) === highlightPlaceId && (
                      <div
                        aria-hidden
                        className="absolute inset-y-0 -z-10 pointer-events-none transition-opacity duration-500"
                        style={{
                          left: 20,
                          right: 20,
                          backgroundColor: HIGHLIGHT_COLOR,
                          opacity: isHighlightVisible ? 1 : 0,
                        }}
                      />
                    )}
                    {index > 0 && (
                      <div className="flex h-1px w-full px-20px box-border">
                        <div className="min-h-px flex-1 bg-divider-divider-1" />
                      </div>
                    )}
                    <RankingItem
                      ranking={ranking}
                      onClick={handleRankingItemClick}
                      isEditMode={isEditMode}
                      // 편집(드래그)은 실시간 위치(index+1). 그 외엔 '카테고리 내 점수 등수'로 고정
                      // → 거리/날짜 정렬해도 순위 숫자는 그 카테고리 안의 점수 등수(1~N) 그대로.
                      displayRank={
                        isEditMode
                          ? index + 1
                          : categoryRankMap.get(ranking.id) ?? index + 1
                      }
                      hideScore={shouldHideScore}
                    />
                  </div>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
          </>
        )}
      </div>
    )
  }

  const renderAppBar = () => {
    return (
      <AppBar
        title="경험한 장소"
        onBack={isOtherUserRanking ? () => navigate(-1) : undefined}
        rightElement={
          isOtherUserRanking ? undefined : (
            <button
              type="button"
              onClick={handleEditClick}
              className={`
            text-style-button1
            text-textColorText2
            cursor-pointer
            hover:opacity-70
            px-16px
            h-48px
            whitespace-nowrap
          `}
            >
              {isEditMode ? '저장' : '수정'}
            </button>
          )
        }
        isBottomBorder
      />
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden">
        {/* 헤더 */}
        {renderAppBar()}

        {/* 콘텐츠 영역 */}
        {isLoadingInitialRankings
          ? renderLoadingSpinner()
          : renderRankingList()}
      </div>

      {/* 바텀 네비게이션 공간 */}
      <div
        className="w-full"
        style={{ height: `${BOTTOM_NAV_HEIGHT}px` }}
      ></div>
      <BottomNav activePath={FEED_NAV_ITEM.path} />

      {/* 랭킹 액션 모달 */}
      {/* 랭킹 항목 액션 바텀시트 */}
      <TextListBottomSheet
        isOpen={isActionModalOpen}
        onClose={handleCloseModal}
        items={[
          { text: '기록 보기', onClick: handleViewReview },
          { text: '기록 수정', onClick: handleReviewRewrite },
          { text: '기록 삭제', onClick: handleDeleteReview },
        ]}
      />

      {/* 카테고리 선택 바텀 시트 */}
      <TextListBottomSheet
        isOpen={isCategoryBottomSheetOpen}
        onClose={() => setIsCategoryBottomSheetOpen(false)}
        items={getAllCategories().map((cat) => ({
          text: cat.name,
          onClick: () => handleCategorySelect(cat.code),
          isSelected: selectedCategory === cat.code,
        }))}
      />

      {/* 정렬 옵션 바텀 시트 */}
      <TextListBottomSheet
        isOpen={isSortBottomSheetOpen}
        onClose={() => setIsSortBottomSheetOpen(false)}
        items={[
          {
            text: '점수',
            onClick: () => handleSortSelect('rating'),
            isSelected: sortType === 'rating',
          },
          {
            text: '추가한 날짜',
            onClick: () => handleSortSelect('date'),
            isSelected: sortType === 'date',
          },
          {
            text: '거리 순',
            onClick: () => handleSortSelect('distance'),
            isSelected: sortType === 'distance',
          },
        ]}
      />
    </div>
  )
}

export default MainRanking
